// src/Pages/TermsOfService.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, Scale, Layers, CreditCard, UserCheck, Shield } from 'lucide-react';

const TermsOfService: React.FC = () => {
  return (
    <section className="relative min-h-screen bg-white py-20 px-4 sm:px-6 lg:px-8 font-[Urbanist] overflow-hidden">
      
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* Top Hero Section: Left Content, Right Image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left Content */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-600 text-sm font-bold tracking-wide mb-6">
              <Shield size={16} /> User Agreement
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mb-6 leading-tight">
              Terms of <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500">Service</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Please read these terms carefully before using Motilal Algo. By accessing or using our platform, you agree to be bound by these terms and all applicable laws and regulations.
            </p>
          </motion.div>

          {/* Right Image */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            {/* <div className="absolute inset-0  -z-10"></div> */}
            {/* Replace the src below with your actual image path */}
            <img 
              src="https://www.cookieyes.com/wp-content/uploads/2026/02/Terms-and-Conditions-vs-Terms-of-Use-vs-Terms-of-Service.png" 
              alt="Terms of Service" 
              className=" object-cover w-full h-[400px] md:h-[500px] "
            />
          </motion.div>
        </div>

        {/* Bottom Content Sections */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {/* Section 1 */}
          <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-slate-100/50 hover:shadow-2xl transition-shadow">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <div className="p-3 rounded-xl bg-blue-50 text-blue-600"><UserCheck size={24} /></div>
              1. Acceptance & Eligibility
            </h2>
            <p className="text-slate-600 leading-relaxed">
              By using Motilal Algo's services, you agree to our terms. To use our platform, you must be <strong>18+ years of age</strong> and possess valid Indian identification documents (PAN, Aadhaar) along with an active Demat Account.
            </p>
          </div>

          {/* Section 2 */}
          <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-slate-100/50 hover:shadow-2xl transition-shadow">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600"><Layers size={24} /></div>
              2. Services Offered
            </h2>
            <div className="flex flex-col gap-3 mt-4">
              {['Automated Algo Execution (NSE/BSE)', 'Nifty & BankNifty Options Strategies', 'Real-time Market Dashboards', 'Margin Funding & Intraday Tools'].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-emerald-500 shrink-0" />
                  <span className="text-slate-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3 */}
          <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-slate-100/50 hover:shadow-2xl transition-shadow">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <div className="p-3 rounded-xl bg-purple-50 text-purple-600"><CreditCard size={24} /></div>
              3. Fees & Charges
            </h2>
            <div className="space-y-3 mt-4">
              <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                <span className="text-slate-600 font-medium">Account Opening</span>
                <span className="font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full text-sm">₹0 (Free)</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                <span className="text-slate-600 font-medium">AMC (Annual Maintenance)</span>
                <span className="font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full text-sm">₹0 (Lifetime)</span>
              </div>
              <div className="flex justify-between items-center pt-1">
                <span className="text-slate-600 font-medium">Trading Brokerage</span>
                <span className="font-bold text-blue-600 text-sm">As per selected plan</span>
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-slate-100/50 hover:shadow-2xl transition-shadow">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <div className="p-3 rounded-xl bg-orange-50 text-orange-600"><Scale size={24} /></div>
              4. Jurisdiction & Disputes
            </h2>
            <p className="text-slate-600 leading-relaxed">
              In the event of any legal dispute or proceedings, all matters will be strictly subject to the jurisdiction of the courts located in <strong>Bhopal (Madhya Pradesh)</strong>. We strictly adhere to and comply with all applicable Indian Laws.
            </p>
          </div>
        </motion.div>

        {/* Bottom Note Box */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-8 bg-blue-50 border border-blue-100 p-6 rounded-2xl flex gap-4 items-start"
        >
          <FileText className="text-blue-600 shrink-0 mt-1" size={24} />
          <p className="text-blue-900/80 font-medium leading-relaxed">
            Motilal Algo reserves the right to update or modify these terms without prior notice. Any significant changes will be communicated to you via our website announcements or registered email address.
          </p>
        </motion.div>

      </div>
    </section>
  );
};

export default TermsOfService;